import React from 'react';
import { Avatar, Badge, styled, keyframes } from '@mui/material';
import { Agent } from '../../stores/chatStore';

interface AgentAvatarProps {
  agent: Agent;
  size?: number;
  showStatus?: boolean;
}

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const StyledBadge = styled(Badge, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status?: string }>(({ theme, status }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: 
      status === 'thinking' || status === 'typing' ? theme.palette.warning.main :
      status === 'error' ? theme.palette.error.main :
      theme.palette.success.main,
    color: theme.palette.common.white,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    width: 12,
    height: 12,
    borderRadius: '50%',
    ...(status === 'typing' && {
      animation: `${pulse} 1.4s infinite ease-in-out`,
    }),
    '&::after': {
      position: 'absolute',
      top: -1,
      left: -1,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: status === 'thinking' ? `${pulse} 1.4s infinite ease-in-out` : 'none',
      border: status === 'thinking' ? '1px solid currentColor' : 'none',
      content: '""',
    },
  },
}));

const AgentAvatar: React.FC<AgentAvatarProps> = ({ agent, size = 40, showStatus = false }) => {
  const getInitial = () => {
    return (agent.custom_name || agent.name).charAt(0).toUpperCase();
  };

  const avatarElement = (
    <Avatar
      src={agent.avatar_url}
      sx={{
        width: size,
        height: size,
        bgcolor: agent.avatar_url ? 'transparent' : (agent.color || agent.avatar_fallback_color),
        fontSize: size * 0.5,
        fontWeight: 600,
      }}
    >
      {!agent.avatar_url && getInitial()}
    </Avatar>
  );

  if (showStatus && agent.status && agent.status !== 'idle') {
    return (
      <StyledBadge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        variant="dot"
        status={agent.status}
      >
        {avatarElement}
      </StyledBadge>
    );
  }

  return avatarElement;
};

export default AgentAvatar;